import { z } from "zod";

export const impactSchema = z.object({
	description: z.string(),
	icon: z.string(),
	id: z.number(),
	name: z.string(),
});

export const companyDataSchema = z.object({
	_org: z.object({
		id: z.number(),
		name: z.string(),
		org: z.object({
			active: z.number(),
			city: z.string().nullable(),
			claimed: z.number(),
			country_id: z.number(),
			created_at: z.string(),
			daily_auto_order_limit: z.string(),
			description: z.string().nullable(),
			domain: z.string(),
			external_id: z.string(),
			fax: z.string().nullable(),
			id: z.number(),
			logo: z.object({
				cdn_url: z.string(),
				id: z.number(),
				url: z.string(),
			}),
			name: z.string(),
			notes: z.string().nullable(),
			org_type: z.string(),
			phone: z.string().nullable(),
			postal_code: z.string().nullable(),
			private: z.boolean(),
			public_id: z.string(),
			state: z.string().nullable(),
			street: z.string().nullable(),
			updated_at: z.string(),
		}),
		org_id: z.null(),
		type: z.string(),
		user_id: z.null(),
	}),
	_result: z.object({
		http_code: z.number(),
	}),
	_view: z.object({
		id: z.null(),
		type: z.null(),
	}),
	data: z.object({
		countries: z.array(
			z.object({
				id: z.number(),
				name: z.string(),
				regions: z.array(
					z.object({
						id: z.number(),
						name: z.string(),
					}),
				),
			}),
		),
		impacts: z.object({
			area_planted: impactSchema,
			area_planted_designated_projected_stat: z.number(),
			area_planted_ordered_projected_stat: z.number(),
			area_planted_possible_stat: z.number(),
			area_planted_projected_stat: z.number(),
			area_planted_stat: z.number(),
			co2: impactSchema,
			co2_designated_projected_stat: z.number(),
			co2_ordered_projected_stat: z.number(),
			co2_possible_stat: z.number(),
			co2_projected_stat: z.number(),
			co2_stat: z.number(),
			ocean_area_planted: impactSchema,
			ocean_area_planted_designated_projected_stat: z.number(),
			ocean_area_planted_ordered_projected_stat: z.number(),
			ocean_area_planted_possible_stat: z.number(),
			ocean_area_planted_projected_stat: z.number(),
			ocean_area_planted_stat: z.number(),
			working_days: z.object({
				description: z.string(),
				icon: z.string(),
				id: z.number(),
				name: z.string(),
			}),
			working_days_designated_projected_stat: z.number(),
			working_days_ordered_projected_stat: z.number(),
			working_days_possible_stat: z.number(),
			working_days_projected_stat: z.number(),
			working_days_stat: z.number(),
			yield: impactSchema,
			yield_designated_projected_stat: z.number(),
			yield_ordered_projected_stat: z.number(),
			yield_possible_stat: z.number(),
			yield_projected_stat: z.number(),
			yield_stat: z.number(),
		}),
		kelp_designated: z.number(),
		kelp_legacy: z.number(),
		kelp_ordered: z.number(),
		kelp_planted: z.number(),
		trees_by_forest_types: z.array(
			z.object({
				forest_type_id: z.number(),
				trees_designated: z.number(),
				trees_legacy: z.number(),
				trees_ordered: z.number(),
				trees_planted: z.number(),
				type: z.union([z.literal("Trees"), z.literal("Kelp")]),
			}),
		),
		trees_designated: z.number(),
		trees_legacy: z.number(),
		trees_ordered: z.number(),
		trees_planted: z.number(),
	}),
});

export type CompanyDataResponse = z.infer<typeof companyDataSchema>;
export type Country = CompanyDataResponse["data"]["countries"][number];

export type RawForestType =
	CompanyDataResponse["data"]["trees_by_forest_types"][number];
